import React, { useEffect, useState } from 'react';
import { ChoiceCard, DropdownMenu, Input, Label, SparkIcon, Text, TooltipTrigger } from '@spark';

/** Section heading inside the Download Model modal (Figma: lead/semibold). */
export function SectionTitle({ children, description = null }) {
  return (
    <div className="configure-deployment-modal__section-heading">
      <Text as="h3" variant="lead-semibold" color="primary" className="configure-deployment-modal__section-title">
        {children}
      </Text>
      {description ? (
        <Text as="p" variant="base-regular" color="secondary" className="configure-deployment-modal__section-desc">
          {description}
        </Text>
      ) : null}
    </div>
  );
}

/** Field label text with a circle-help icon that explains the field on hover. */
export function HelpLabel({ text, tooltip }) {
  if (!tooltip) return text;
  return (
    <span className="configure-deployment-modal__help-label">
      {text}
      <TooltipTrigger text={tooltip} placement="top">
        <span className="configure-deployment-modal__help-icon" aria-label={tooltip} role="img">
          <SparkIcon slug="circle-help" size="small" />
        </span>
      </TooltipTrigger>
    </span>
  );
}

/**
 * Single-select Spark DropdownMenu with an external label (so the label can
 * carry a help tooltip, which `DropdownMenu.label` — a plain string — cannot).
 * ``options`` are `{ value, label }`; ``onChange`` receives the picked value.
 */
export function LabeledDropdown({
  label,
  tooltip,
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  searchable = false,
  danger = false,
  className = '',
}) {
  const selected = options.find((opt) => opt.value === value) || null;
  const selectedLabel = selected ? String(selected.label) : undefined;
  const items = options.map((opt) => ({
    label: String(opt.label),
    onClick: () => onChange(opt.value),
  }));
  return (
    <div
      className={['configure-deployment-modal__field', danger ? 'configure-deployment-modal__field--danger' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <Label label={<HelpLabel text={label} tooltip={tooltip} />} />
      <DropdownMenu
        fill
        label={label}
        showLabel={false}
        placeholder={placeholder}
        text={selectedLabel}
        value={selectedLabel}
        items={items}
        disabled={disabled}
        searchable={searchable}
        className="configure-deployment-modal__dropdown"
        panelClassName="configure-deployment-modal__dropdown-panel"
      />
    </div>
  );
}

/** Small inline error line under a field. */
export function FieldError({ children }) {
  if (!children) return null;
  return (
    <Text as="p" variant="detail-regular" role="alert" className="configure-deployment-modal__field-error">
      {children}
    </Text>
  );
}

/** Small inline hint line under a field. */
export function FieldHint({ children }) {
  if (!children) return null;
  return (
    <Text as="p" variant="detail-regular" color="secondary" className="configure-deployment-modal__field-hint">
      {children}
    </Text>
  );
}

/**
 * "Number of Instances" (Figma 9952:24284): Triton model instances on the
 * machine. Keeps the raw text while typing and commits whole numbers ≥ 1.
 */
export function InstancesField({ value, onChange, errorMessage = null, id }) {
  const [draft, setDraft] = useState(String(value ?? 1));
  useEffect(() => {
    setDraft(String(value ?? 1));
  }, [value]);

  const commit = (raw) => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isInteger(parsed) && parsed >= 1) onChange(parsed);
  };

  return (
    <div className="configure-deployment-modal__instances-field">
      <Input
        id={id}
        type="number"
        min={1}
        step={1}
        inputMode="numeric"
        label={
          <HelpLabel text="Number of Instances" tooltip="Number of Triton model instances on this machine" />
        }
        placeholder="1"
        value={draft}
        danger={Boolean(errorMessage)}
        onChange={(event) => {
          setDraft(event.target.value);
          commit(event.target.value);
        }}
        onBlur={() => {
          const parsed = Number.parseInt(draft, 10);
          if (!Number.isInteger(parsed) || parsed < 1) {
            setDraft('1');
            onChange(1);
          }
        }}
      />
      <FieldError>{errorMessage}</FieldError>
    </div>
  );
}

/**
 * Row(s) of mutually exclusive ChoiceCards. ``rows`` groups option values per
 * row (Figma 2×2 prediction grid); otherwise all options share one row.
 */
export function ChoiceCardGroup({
  options,
  value,
  onChange,
  rows = null,
  icons = {},
  size = 'default',
  ariaLabel,
}) {
  const byValue = Object.fromEntries(options.map((opt) => [opt.value, opt]));
  const rowGroups = rows
    ? rows.map((rowValues) => rowValues.map((v) => byValue[v]).filter(Boolean))
    : [options];
  return (
    <div className="configure-deployment-modal__card-rows" role="group" aria-label={ariaLabel}>
      {rowGroups.map((rowOptions) => (
        <div key={rowOptions.map((opt) => opt.value).join('-')} className="configure-deployment-modal__card-row">
          {rowOptions.map((opt) => (
            <ChoiceCard
              key={opt.value}
              size={size}
              iconSlug={icons[opt.value]}
              label={opt.title ?? opt.label}
              description={opt.description}
              selected={value === opt.value}
              onClick={() => onChange(opt.value)}
              data-value={opt.value}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
